import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { Mark } from "./Mark";
import { PageHero } from "./PageHero";
import { CaseSnapshot } from "./ProductVisuals";
import type { CaseStudy } from "../data/caseStudies";
import { ArrowRight, CheckCircle } from "lucide-react";

const H2 = "text-[28px] lg:text-[32px] leading-tight font-bold tracking-tight text-dark-indigo";

/**
 * One anonymous case study, in the standard order: at a glance → client → challenge → goals →
 * how we delivered → solution on TurfAI → how it runs → results → what's next.
 * The hero shows the before/after snapshot, so no case study repeats its solution page's screen.
 */
export function CaseStudyPage({ data, onNavigate, onNavigateToDemo }: { data: CaseStudy; onNavigate: (p: string) => void; onNavigateToDemo: () => void }) {
  const glance: [string, string][] = [
    ["Client", data.glance.client],
    ["Scale", data.glance.scale],
    ["Region", data.glance.region],
    ["Deployment", data.glance.deployment],
    ["Timeline", data.glance.timeline],
  ];
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        crumb={<><PageLink page="resources" onNavigate={onNavigate} className="font-semibold text-royal-indigo hover:text-dark-indigo">Case studies</PageLink><span className="mx-1.5">/</span>{data.sector}</>}
        title={data.headline}
        lede={data.standfirst}
        visual={<CaseSnapshot sector={data.sector} rows={data.snapshot} capabilities={data.capabilities} />}
      />

      {/* Highlights */}
      <section className="border-b border-softblack-tint-20 bg-white">
        <div className="container mx-auto grid grid-cols-2 gap-6 px-4 py-10 lg:grid-cols-4">
          {data.highlights.map((h) => (
            <div key={h.label}>
              <div className="font-display text-[34px] lg:text-[40px] font-bold leading-tight text-dark-indigo">{h.metric}</div>
              <p className="mt-1 text-[14px] text-soft-black">{h.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* At a glance + client */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto grid gap-12 px-4 lg:grid-cols-[1fr_2fr]">
          <aside className="h-fit rounded-lg border border-softblack-tint-20 p-6">
            <h2 className="font-display text-[19px] font-semibold text-dark-indigo">At a glance</h2>
            <dl className="mt-4 space-y-4">
              {glance.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[13px] font-semibold text-royal-indigo">{k}</dt>
                  <dd className="mt-0.5 text-[15px] text-soft-black">{v}</dd>
                </div>
              ))}
            </dl>
          </aside>
          <div>
            <h2 className={H2}>The client</h2>
            <p className="mt-4 text-[18px] leading-relaxed text-soft-black">{data.context}</p>

            <h2 className={`${H2} mt-14`}>The challenge</h2>
            <p className="mt-4 text-[18px] leading-relaxed text-soft-black">{data.challenge.summary}</p>
            <ul className="mt-6 space-y-3">
              {data.challenge.points.map((c) => (
                <li key={c} className="border-l-2 border-indigo-tint-50 pl-4 text-[16px] leading-relaxed text-soft-black">{c}</li>
              ))}
            </ul>

            <h2 className={`${H2} mt-14`}>What they needed</h2>
            <ul className="mt-6 space-y-3">
              {data.goals.map((g) => (
                <li key={g} className="flex items-start gap-3 text-[16px] text-soft-black">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-royal-indigo" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How we delivered */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <h2 className={H2}>How we delivered</h2>
          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {data.approach.map((a, i) => (
              <li key={a.phase} className="rounded-lg border border-softblack-tint-20 bg-white p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-display text-[22px] font-bold text-royal-indigo">{i + 1}</span>
                  <span className="text-[13px] font-semibold text-soft-black">{a.duration}</span>
                </div>
                <h3 className="mt-2 font-display text-[19px] font-semibold text-dark-indigo">{a.phase}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-soft-black">{a.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Solution + how it runs */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto grid gap-12 px-4 lg:grid-cols-[3fr_2fr]">
          <div>
            <h2 className={H2}>The solution on <Mark>TurfAI</Mark></h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {data.solution.map((s) => (
                <div key={s.title} className="rounded-lg border border-softblack-tint-20 p-5">
                  <h3 className="font-display text-[18px] font-semibold text-dark-indigo">{s.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-soft-black">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className={H2}>How it runs, day to day</h2>
            <ol className="mt-8 space-y-4">
              {data.howItRuns.map((step, i) => (
                <li key={step} className="grid grid-cols-[32px_1fr] gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-indigo-tint-20 font-display text-[14px] font-bold text-royal-indigo">{i + 1}</span>
                  <span className="pt-1 text-[15px] leading-relaxed text-soft-black">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Result + next */}
      <section className="py-16 band-surface border-y border-softblack-tint-20">
        <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[2fr_1fr]">
          <div>
            <p className="text-sm font-semibold text-royal-indigo">The result</p>
            <p className="mt-3 font-display text-[24px] lg:text-[28px] font-semibold leading-snug text-dark-indigo">{data.result}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-royal-indigo">What's next</p>
            <p className="mt-3 text-[17px] leading-relaxed text-soft-black">{data.next}</p>
            <PageLink page={data.related.page} onNavigate={onNavigate} className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-bold text-royal-indigo hover:text-dark-indigo">
              {data.related.label} <ArrowRight className="h-4 w-4" />
            </PageLink>
          </div>
        </div>
      </section>

      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[32px] lg:text-[40px] leading-tight font-bold tracking-tight mb-6">Your workflow could be the <Mark>next story</Mark></h2>
          <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>Schedule a demo</Button>
        </div>
      </section>
    </div>
  );
}
